import {useForm, Controller} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {useSnackbar} from '../../context/SnackbarContextProvider';
import {handleLoginService} from '../../services/users';
import {useMutation} from '@tanstack/react-query';
import AppTextInput from '../../components/common/AppTextInput';
import AppButton from '../../components/common/AppButton';
import {SVG} from '../../constants/assetLists';
import {Checkbox} from '@mui/material';
import {useNavigate} from 'react-router';
import {
  findFirstAccessibleRoute,
  requestPermissionAndGetToken,
} from '../../utils';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {getFcmDeviceFields, markFcmTokenRegistered} from '../../utils/fcmToken';

type FormValues = {
  email: string;
  password: string;
  showPassword: boolean;
  rememberMe: boolean;
};

const zodSchema = z.object({
  email: z.email('כתובת מייל לא תקינה').nonempty('שדה חובה!'),
  password: z.string({error: 'שדה חובה!'}).nonempty('שדה חובה!'),
  showPassword: z.boolean(),
  rememberMe: z.boolean(),
});

const Login = () => {
  const {showSnackbar} = useSnackbar();

  const {i18n} = useTranslation();

  const navigate = useNavigate();

  const {
    control,
    setValue,
    watch,
    reset,
    handleSubmit,
    formState: {errors},
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
      showPassword: false,
      rememberMe: false,
    },
    resolver: zodResolver(zodSchema),
  });

  const email = watch('email');
  const password = watch('password');
  const showPassword = watch('showPassword');
  const rememberMe = watch('rememberMe');

  useEffect(() => {
    const isAuthenticated = !!localStorage.getItem('loggedIn');
    if (isAuthenticated) {
      const redirectPath = findFirstAccessibleRoute();
      navigate(redirectPath, {replace: true});
    }
  }, []);

  useEffect(() => {
    const isRememberMe = !!localStorage.getItem('rememberMe');
    if (isRememberMe) {
      const storedEmail = localStorage.getItem('email') || '';
      const storedPassword = localStorage.getItem('password') || '';
      setValue('email', storedEmail);
      setValue('password', storedPassword);
      setValue('rememberMe', true);
    }
  }, []);

  const {mutate: handleLogin, isPending} = useMutation({
    mutationFn: handleLoginService,
    onSuccess: (data, variables) => {
      if (variables?.fcmToken) {
        markFcmTokenRegistered(variables.fcmToken);
      }
      localStorage.setItem('userToken', data?.data?.token);
      localStorage.setItem('user', JSON.stringify(data?.data?.user));
      localStorage.setItem(
        'userType',
        JSON.stringify(data?.data?.user?.userType),
      );
      localStorage.setItem(
        'userName',
        `${data?.data?.user?.firstName} ${data?.data?.user?.lastName}`,
      );
      localStorage.setItem('userId', data?.data?.user?._id);
      localStorage.setItem('loggedIn', 'true');

      if (rememberMe) {
        localStorage.setItem('email', email);
        localStorage.setItem('password', password);
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('email');
        localStorage.removeItem('password');
        localStorage.removeItem('rememberMe');
      }

      const redirectPath = findFirstAccessibleRoute();

      reset();
      showSnackbar({
        type: 'success',
        message: data?.message ?? 'התחברת בהצלחה',
      });

      navigate(redirectPath, {replace: true});
    },
  });

  const onSubmit = async (data: FormValues) => {
    let fcmToken: string | null = null;
    try {
      fcmToken = await requestPermissionAndGetToken(false);
    } catch {
      /* notification errors should never block login */
    }
    const payload = {
      email: data.email?.toLowerCase(),
      password: data.password,
      lang: i18n.language,
      ...(fcmToken && getFcmDeviceFields(fcmToken)),
    };
    
    handleLogin(payload);
  };

  const handleValueChange = (field: keyof FormValues, value: any) => {
    setValue(field, value, {shouldValidate: true, shouldDirty: true});
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage:
          'radial-gradient(circle,  #85FFF8, #e6fffe, #e6fffe, #e6fffe)',
      }}>
      <div className="w-[334px]">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-8"
          dir="rtl">
          <div className="flex flex-col gap-6">
            <Controller
              control={control}
              name="email"
              render={({field}) => (
                <AppTextInput
                  label="דואל"
                  className={`bg-transparent text-[20px] text-darkerGray
                 ${
                   errors?.email
                     ? 'border-raspberryRed focus:border-raspberryRed '
                     : 'border-lightDarkGray focus:!border-deepAqua'
                 }
                `}
                  labelClassName={`text-[14px] text-darkerGray
                ${
                  errors?.email
                    ? 'group-focus-within:text-raspberryRed'
                    : 'group-focus-within:!text-deepAqua'
                } `}
                  required
                  {...field}
                  error={errors.email?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="password"
              render={({field}) => (
                <AppTextInput
                  label="סיסמה"
                  type={showPassword ? 'text' : 'password'}
                  className={`bg-transparent text-[20px] text-darkerGray
                 ${
                   errors?.password
                     ? 'border-raspberryRed focus:border-raspberryRed '
                     : 'border-lightDarkGray focus:!border-deepAqua'
                 }
                `}
                  labelClassName={`text-[14px] text-darkerGray
                ${
                  errors?.password
                    ? 'group-focus-within:text-raspberryRed'
                    : 'group-focus-within:!text-deepAqua'
                } `}
                  endIcon={
                    showPassword ? (
                      <img
                        src={SVG.eyeOnIcon}
                        alt="icon"
                        onClick={() => handleValueChange('showPassword', false)}
                        className="w-[36.67px] h-[25px] object-contain"
                      />
                    ) : (
                      <img
                        src={SVG.eyeOffIcon}
                        alt="icon"
                        onClick={() => handleValueChange('showPassword', true)}
                        className="w-[36.67px] h-[33px] object-contain"
                      />
                    )
                  }
                  required
                  {...field}
                  error={errors.password?.message}
                />
              )}
            />
          </div>

          <div className="flex justify-start">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-[24px] underline text-darkBlue hover:opacity-80">
              שכחתי סיסמא
            </button>
          </div>

          <div className="flex items-center gap-2">
            <Controller
              control={control}
              name="rememberMe"
              render={({field}) => (
                <Checkbox
                  color="secondary"
                  size="large"
                  sx={{p: 0}}
                  checked={field.value}
                  value={field.value}
                  onChange={e => {
                    field.onChange(e.target.checked);
                  }}
                />
              )}
            />
            <span className="text-[24px] text-darkBlue">השאר אותי מחובר</span>
          </div>

          <AppButton
            variant="blue"
            className="justify-center text-[24px] font-normal"
            py="14px"
            isLoading={isPending}
            disabled={!email || !password}
            type="submit">
            כניסה למערכת
          </AppButton>
        </form>
      </div>

      <div className="absolute bottom-[100px]">
        <img
          src={SVG.appLogoIcon}
          alt="brand"
          className="w-[140px] h-6 object-contain"
        />
      </div>
    </div>
  );
};

export default Login;
